import type { FC } from 'react';
import { Link } from 'react-router-dom';

import type { NavItem, NavSection } from '@/types/nav.types';
import type { PublicUser } from '@/types/user.types';
import { ADMIN_ROLE_LABELS } from '@/types/user.types';
import { cn } from '@/utils/cn.util';

interface SidebarProps {
  sections: NavSection[];
  activePath: string;
  user: PublicUser | undefined;
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
}

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const NavRow: FC<{ item: NavItem; active: boolean }> = ({ item, active }) => {
  const base =
    'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors';

  if (item.disabled || !item.to) {
    return (
      <div
        className={cn(base, 'cursor-not-allowed text-white/25')}
        title="Coming soon"
        aria-disabled
      >
        <span className="w-5 text-center">{item.icon}</span>
        <span>{item.label}</span>
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-white/20">
          Soon
        </span>
      </div>
    );
  }

  return (
    <Link
      to={item.to}
      className={cn(
        base,
        active
          ? 'bg-brand-500/20 text-white'
          : 'text-white/55 hover:bg-[var(--color-sidebar-hover)] hover:text-white/90',
      )}
    >
      {active ? (
        <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-brand-500" />
      ) : null}
      <span className="w-5 text-center">{item.icon}</span>
      <span>{item.label}</span>
      {typeof item.badge === 'number' ? (
        <span className="ml-auto rounded-full bg-brand-500 px-2 py-0.5 text-[10px] font-semibold text-white">
          {item.badge}
        </span>
      ) : null}
    </Link>
  );
};

const Sidebar: FC<SidebarProps> = ({ sections, activePath, user, isOpen, onClose, onLogout }) => (
  <>
    {/* Mobile backdrop — tapping outside closes the sidebar */}
    <div
      className={cn(
        'fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 md:hidden',
        isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      onClick={onClose}
    />

    <aside
      className={cn(
        'flex w-[230px] shrink-0 flex-col bg-[var(--color-sidebar)] text-white',
        // Mobile: fixed overlay that slides in/out from left
        'fixed inset-y-0 left-0 z-50 transition-transform duration-300 ease-in-out',
        isOpen ? 'translate-x-0' : '-translate-x-full',
        // Desktop: relative (in-flow), always visible, no translate
        'md:relative md:translate-x-0 md:transition-none',
      )}
    >
      <div className="flex items-center gap-3 border-b border-white/5 px-5 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-brand-500 text-base font-extrabold text-white">
          S
        </div>
        <span className="text-[17px] font-bold tracking-tight">Shoutt</span>
        <span className="ml-auto rounded-full bg-brand-500 px-2 py-0.5 text-[9px] font-semibold uppercase">
          Admin
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section) => (
          <div key={section.label} className="mb-4">
            <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-wider text-white/25">
              {section.label}
            </p>
            <div className="flex flex-col gap-0.5">
              {section.items.map((item) => (
                <NavRow
                  key={item.label}
                  item={item}
                  active={Boolean(item.to && item.to === activePath)}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-3 pb-3">
        <button
          type="button"
          onClick={onLogout}
          className="mb-3 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-white/55 transition-colors hover:bg-[var(--color-sidebar-hover)] hover:text-white"
        >
          <span className="w-5 text-center">🚪</span>
          <span>Logout</span>
        </button>

        {user ? (
          <div className="flex items-center gap-3 rounded-lg bg-white/5 px-3 py-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-400 text-xs font-bold text-white">
              {initialsOf(user.name)}
            </div>
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold text-white">{user.name}</p>
              <p className="truncate text-[10px] text-white/45">{ADMIN_ROLE_LABELS[user.role]}</p>
            </div>
          </div>
        ) : null}
      </div>
    </aside>
  </>
);

export default Sidebar;
